import type { SupabaseClient } from '@supabase/supabase-js';
import {
  moneyFromMajor,
  moneyToMajor,
  sumMoney,
  type NotificationCandidate,
} from '@expense-assistant/finance-core';
import type {
  AppAccount,
  AppBudgetGroup,
  AppCategory,
  AppData,
  AppDebt,
  AppGoal,
  AppPlan,
  AppTransaction,
} from './data';

function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}

interface AccountRow {
  id: string;
  name: string;
  account_type: string;
  current_balance_minor: string | number;
  is_spendable: boolean;
  is_protected: boolean;
  minimum_balance: string | number;
}

interface TransactionRow {
  id: string;
  transaction_type: AppTransaction['type'];
  description: string | null;
  amount: string | number;
  transaction_date: string;
  account_id: string | null;
  from_account_id: string | null;
  to_account_id: string | null;
  categories: { name?: string } | null;
  budget_groups: { name?: string } | null;
  notes: string | null;
}

export interface FinanceRepository {
  loadWorkspace(from: string, to: string, asOf: string): Promise<AppData>;
  listAccounts(): Promise<AppAccount[]>;
  listTransactions(from: string, to: string): Promise<AppTransaction[]>;
  createTransaction(transaction: AppTransaction): Promise<string>;
  createPlannedItem(
    plan: AppPlan,
    category: string,
    notes: string,
    recurring: boolean,
  ): Promise<string>;
  createAccount(account: AppAccount, openingDate: string): Promise<string>;
  createGoal(goal: AppGoal, allocationDate: string): Promise<string>;
  createDebt(debt: AppDebt, createdDate: string): Promise<string>;
  settleDebt(
    debtId: string,
    amountMinor: number,
    accountId: string,
    date: string,
    idempotencyKey: string,
    notes?: string,
  ): Promise<string>;
  settlePlan(plan: AppPlan, date: string, idempotencyKey: string): Promise<string>;
  recordGoalAllocation(
    goalId: string,
    kind: 'ALLOCATE' | 'RELEASE',
    amountMinor: number,
    date: string,
    idempotencyKey: string,
  ): Promise<string>;
  markNotificationRead(notificationId: string): Promise<void>;
  markAllNotificationsRead(): Promise<void>;
  updateSafetyBuffer(amountMinor: number): Promise<void>;
  refreshNotifications(candidates: NotificationCandidate[]): Promise<void>;
  createCategory(category: AppCategory): Promise<void>;
  createBudgetGroup(group: AppBudgetGroup): Promise<void>;
  archiveCategory(id: string): Promise<void>;
  archiveBudgetGroup(id: string): Promise<void>;
  renameCategory(id: string, name: string): Promise<void>;
  renameBudgetGroup(id: string, name: string): Promise<void>;
  exportBackup(): Promise<unknown>;
  previewBackup(backup: unknown): Promise<unknown>;
  restoreBackup(
    backup: unknown,
    strategy: 'MERGE' | 'REPLACE',
    idempotencyKey: string,
  ): Promise<unknown>;
}

export class SupabaseFinanceRepository implements FinanceRepository {
  constructor(private readonly client: SupabaseClient) {}

  loadWorkspace(from: string, to: string, asOf: string): Promise<AppData> {
    return loadWorkspace(this.client, this, from, to, asOf);
  }

  async listAccounts(): Promise<AppAccount[]> {
    const { data, error } = await this.client
      .from('account_balances')
      .select('*')
      .eq('is_active', true)
      .order('name');
    if (error) throw error;
    return (data as unknown as AccountRow[]).map((row) => ({
      id: String(row.id),
      name: String(row.name),
      type: String(row.account_type),
      balance: Number(row.current_balance_minor),
      spendable: Boolean(row.is_spendable),
      protected: Boolean(row.is_protected),
      minimumBalance: moneyFromMajor(String(row.minimum_balance)),
    }));
  }

  async listTransactions(from: string, to: string): Promise<AppTransaction[]> {
    const { data, error } = await this.client
      .from('transactions')
      .select('*, categories(name), budget_groups(name)')
      .gte('transaction_date', from)
      .lte('transaction_date', to)
      .eq('lifecycle', 'POSTED')
      .order('transaction_date', { ascending: false });
    if (error) throw error;
    return (data as unknown as TransactionRow[]).map((row) => ({
      id: String(row.id),
      type: row.transaction_type,
      label: String(row.description || row.transaction_type),
      amount: moneyFromMajor(String(row.amount)),
      date: String(row.transaction_date) as AppTransaction['date'],
      accountId: String(row.account_id ?? row.from_account_id),
      ...(row.transaction_type === 'TRANSFER' && row.to_account_id
        ? { toAccountId: row.to_account_id }
        : {}),
      category: String(row.categories?.name ?? 'Uncategorized'),
      group: String(row.budget_groups?.name ?? 'Ungrouped'),
      ...(row.notes ? { notes: row.notes } : {}),
      status: 'COMPLETED',
    }));
  }

  async createTransaction(transaction: AppTransaction): Promise<string> {
    if (transaction.plannedEventId) {
      const response = (await this.client.rpc('record_and_settle_planned_event', {
        p_planned_event_id: transaction.plannedEventId,
        p_amount_minor: transaction.amount,
        p_transaction_date: transaction.date,
        p_idempotency_key: transaction.id,
        p_account_id: transaction.type === 'TRANSFER' ? null : transaction.accountId,
        p_description: transaction.label,
        p_notes: transaction.notes ?? null,
      })) as { data: unknown; error: unknown };
      if (response.error) throw toError(response.error);
      return String(response.data);
    }
    const [categoryResult, groupResult] =
      transaction.type === 'TRANSFER'
        ? [{ data: null }, { data: null }]
        : await Promise.all([
            this.client
              .from('categories')
              .select('id')
              .eq('name', transaction.category)
              .maybeSingle(),
            this.client
              .from('budget_groups')
              .select('id')
              .eq('name', transaction.group)
              .maybeSingle(),
          ]);
    const categoryId = (categoryResult.data as { id?: string } | null)?.id ?? null;
    const budgetGroupId = (groupResult.data as { id?: string } | null)?.id ?? null;
    const request =
      transaction.type === 'TRANSFER'
        ? this.client.rpc('create_transfer', {
            p_from_account_id: transaction.accountId,
            p_to_account_id: transaction.toAccountId,
            p_amount_minor: transaction.amount,
            p_transaction_date: transaction.date,
            p_idempotency_key: transaction.id,
            p_description: transaction.label,
            p_notes: transaction.notes ?? null,
          })
        : this.client.rpc('record_transaction', {
            p_transaction_type: transaction.type,
            p_amount_minor: transaction.amount,
            p_transaction_date: transaction.date,
            p_account_id: transaction.accountId,
            p_idempotency_key: transaction.id,
            p_description: transaction.label,
            p_category_id: categoryId,
            p_budget_group_id: budgetGroupId,
            p_notes: transaction.notes ?? null,
          });
    const response = (await request) as { data: unknown; error: unknown };
    if (response.error) throw toError(response.error);
    return String(response.data);
  }

  async createPlannedItem(
    plan: AppPlan,
    category: string,
    notes: string,
    recurring: boolean,
  ): Promise<string> {
    const response = (await this.client.rpc('create_planned_item', {
      p_transaction_type: plan.type,
      p_amount_minor: plan.amount,
      p_due_date: plan.date,
      p_account_id: plan.type === 'TRANSFER' ? null : plan.accountId,
      p_from_account_id: plan.type === 'TRANSFER' ? plan.accountId : null,
      p_to_account_id: plan.type === 'TRANSFER' ? plan.toAccountId : null,
      p_category_name: plan.type === 'TRANSFER' ? null : category,
      p_budget_group_name: plan.type === 'TRANSFER' ? null : plan.group,
      p_description: plan.label,
      p_notes: notes || null,
      p_commitment_level: plan.mandatory ? 'MANDATORY' : 'DISCRETIONARY',
      p_recurring: recurring,
      p_schedule_kind: 'EXACT_DAY',
      p_effective_end: null,
    })) as { data: unknown; error: unknown };
    if (response.error) throw toError(response.error);
    return String(response.data);
  }

  async createAccount(account: AppAccount, openingDate: string): Promise<string> {
    const allowedTypes: Record<string, string> = {
      Bank: 'BANK',
      Savings: 'SAVINGS',
      Cash: 'CASH',
      Other: 'OTHER',
    };
    const { data, error } = await this.client
      .from('accounts')
      .insert({
        id: account.id,
        name: account.name,
        account_type: allowedTypes[account.type] ?? 'OTHER',
        opening_balance: moneyToMajor(account.balance),
        opening_date: openingDate,
        currency: 'LKR',
        is_spendable: account.spendable,
        is_protected: account.protected,
        minimum_balance: moneyToMajor(account.minimumBalance),
      })
      .select('id')
      .single();
    if (error) throw error;
    return String(data.id);
  }

  async createGoal(goal: AppGoal, allocationDate: string): Promise<string> {
    const response = (await this.client.rpc('create_goal_with_initial_allocation', {
      p_id: goal.id,
      p_name: goal.name,
      p_target_amount_minor: goal.target,
      p_currency: 'LKR',
      p_target_date: goal.targetDate,
      p_priority: goal.priority,
      p_monthly_contribution_minor: goal.monthlyContribution,
      p_contribution_day: goal.monthlyContribution > 0 ? 1 : null,
      p_lifecycle: goal.status,
      p_idempotency_key: crypto.randomUUID(),
      p_initial_allocation_minor: goal.allocated,
      p_allocation_date: allocationDate,
    })) as { data: unknown; error: unknown };
    if (response.error) throw toError(response.error);
    const rows = response.data as Array<{ goal_id: string }> | null;
    const goalId = rows?.[0]?.goal_id;
    if (!goalId) throw new Error('goal creation did not return an id');
    return String(goalId);
  }

  async createDebt(debt: AppDebt, createdDate: string): Promise<string> {
    const { data, error } = await this.client
      .from('debts')
      .insert({
        id: debt.id,
        counterparty: debt.counterparty,
        direction: debt.direction === 'I_OWE' ? 'PAYABLE' : 'RECEIVABLE',
        original_amount: moneyToMajor(debt.originalAmount),
        currency: 'LKR',
        created_date: createdDate,
        due_date: debt.dueDate,
        lifecycle: 'OPEN',
      })
      .select('id')
      .single();
    if (error) throw error;
    return String(data.id);
  }

  async settleDebt(
    debtId: string,
    amountMinor: number,
    accountId: string,
    date: string,
    idempotencyKey: string,
    notes?: string,
  ): Promise<string> {
    const response = (await this.client.rpc('record_debt_payment', {
      p_debt_id: debtId,
      p_amount_minor: amountMinor,
      p_transaction_date: date,
      p_account_id: accountId,
      p_idempotency_key: idempotencyKey,
      p_notes: notes ?? null,
    })) as { data: unknown; error: unknown };
    if (response.error) throw toError(response.error);
    return String(response.data);
  }

  async recordGoalAllocation(
    goalId: string,
    kind: 'ALLOCATE' | 'RELEASE',
    amountMinor: number,
    date: string,
    idempotencyKey: string,
  ): Promise<string> {
    const response = (await this.client.rpc('record_goal_allocation', {
      p_goal_id: goalId,
      p_allocation_kind: kind,
      p_amount_minor: amountMinor,
      p_allocation_date: date,
      p_idempotency_key: idempotencyKey,
    })) as { data: unknown; error: unknown };
    if (response.error) throw toError(response.error);
    return String(response.data);
  }

  async settlePlan(plan: AppPlan, date: string, idempotencyKey: string): Promise<string> {
    const response = (await this.client.rpc('record_and_settle_planned_event', {
      p_planned_event_id: plan.id,
      p_amount_minor: plan.amount,
      p_transaction_date: date,
      p_idempotency_key: idempotencyKey,
      p_account_id: plan.type === 'TRANSFER' ? null : plan.accountId,
    })) as { data: unknown; error: unknown };
    if (response.error) throw toError(response.error);
    return String(response.data);
  }

  async markNotificationRead(notificationId: string): Promise<void> {
    const { error } = await this.client
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('id', notificationId);
    if (error) throw error;
  }

  async markAllNotificationsRead(): Promise<void> {
    const { error } = await this.client.rpc('mark_all_notifications_read');
    if (error) throw error;
  }

  async updateSafetyBuffer(amountMinor: number): Promise<void> {
    const { data: authData, error: authError } = await this.client.auth.getUser();
    if (authError) throw authError;
    if (!authData.user) throw new Error('Authentication required');
    const { error } = await this.client
      .from('user_preferences')
      .update({ safety_buffer: moneyToMajor(amountMinor) })
      .eq('user_id', authData.user.id);
    if (error) throw error;
  }

  async refreshNotifications(candidates: NotificationCandidate[]): Promise<void> {
    await Promise.all(
      candidates.map(async (candidate) => {
        const response = (await this.client.rpc('upsert_notification', {
          p_dedupe_key: candidate.dedupeKey,
          p_notification_type: candidate.kind,
          p_severity: candidate.severity,
          p_title: candidate.finding,
          p_message: `${candidate.evidence} ${candidate.possibleAction}`,
          p_effective_date: candidate.effectiveDate,
          p_subject_type: candidate.kind,
          p_subject_id:
            /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
              candidate.subjectId,
            )
              ? candidate.subjectId
              : null,
          p_context: {
            evidence: candidate.evidence,
            possibleAction: candidate.possibleAction,
          },
          p_action_key: candidate.kind,
          p_generated_rule_version: 1,
        })) as { error: unknown };
        if (response.error) throw toError(response.error);
      }),
    );
  }

  async createCategory(category: AppCategory): Promise<void> {
    const { error } = await this.client.from('categories').insert({
      id: category.id,
      name: category.name,
      applicability: category.applicability,
      is_system_default: false,
      is_active: true,
    });
    if (error) throw error;
  }

  async createBudgetGroup(group: AppBudgetGroup): Promise<void> {
    const { error } = await this.client.from('budget_groups').insert({
      id: group.id,
      name: group.name,
      is_active: true,
    });
    if (error) throw error;
  }

  async archiveCategory(id: string): Promise<void> {
    const { error } = await this.client
      .from('categories')
      .update({ is_active: false })
      .eq('id', id);
    if (error) throw error;
  }

  async archiveBudgetGroup(id: string): Promise<void> {
    const { error } = await this.client
      .from('budget_groups')
      .update({ is_active: false })
      .eq('id', id);
    if (error) throw error;
  }

  async renameCategory(id: string, name: string): Promise<void> {
    const { error } = await this.client.from('categories').update({ name }).eq('id', id);
    if (error) throw error;
  }

  async renameBudgetGroup(id: string, name: string): Promise<void> {
    const { error } = await this.client.from('budget_groups').update({ name }).eq('id', id);
    if (error) throw error;
  }

  async exportBackup(): Promise<unknown> {
    const response = (await this.client.rpc('export_financial_backup')) as {
      data: unknown;
      error: unknown;
    };
    if (response.error) throw toError(response.error);
    return response.data;
  }

  async previewBackup(backup: unknown): Promise<unknown> {
    const response = (await this.client.rpc('preview_financial_backup', { p_backup: backup })) as {
      data: unknown;
      error: unknown;
    };
    if (response.error) throw toError(response.error);
    return response.data;
  }

  async restoreBackup(
    backup: unknown,
    strategy: 'MERGE' | 'REPLACE',
    idempotencyKey: string,
  ): Promise<unknown> {
    const response = (await this.client.rpc('restore_financial_backup', {
      p_backup: backup,
      p_strategy: strategy,
      p_idempotency_key: idempotencyKey,
    })) as { data: unknown; error: unknown };
    if (response.error) throw toError(response.error);
    return response.data;
  }
}

interface PlannedEventRow {
  id: string;
  transaction_type: 'INCOME' | 'EXPENSE' | 'TRANSFER';
  amount: string | number;
  account_id: string | null;
  from_account_id: string | null;
  to_account_id: string | null;
  description: string;
  due_window_start: string;
  due_window_end: string;
  scheduled_date: string | null;
  commitment_level: string;
  lifecycle: string;
  budget_groups: { name?: string } | null;
  planned_event_settlements: Array<{
    amount: string | number;
    transactions: { lifecycle: string } | null;
  }>;
}

interface GoalRow {
  id: string;
  name: string;
  target_amount: string | number;
  allocated_amount: string | number;
  target_date: string | null;
  planned_monthly_contribution: string | number;
  priority: AppGoal['priority'];
  lifecycle: string;
}

interface DebtRow {
  id: string;
  counterparty: string;
  direction: 'PAYABLE' | 'RECEIVABLE';
  original_amount: string | number;
  remaining_amount: string | number;
  due_date: string | null;
}

interface NotificationRow {
  id: string;
  title: string;
  message: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  read_at: string | null;
  created_at: string;
}

async function loadWorkspace(
  client: SupabaseClient,
  repository: Pick<FinanceRepository, 'listAccounts' | 'listTransactions'>,
  from: string,
  to: string,
  asOf: string,
): Promise<AppData> {
  const [
    accounts,
    transactions,
    plansResult,
    goalsResult,
    debtsResult,
    notificationsResult,
    categoriesResult,
    budgetGroupsResult,
    preferencesResult,
  ] = await Promise.all([
    repository.listAccounts(),
    repository.listTransactions(from, to),
    client
      .from('planned_events')
      .select('*, budget_groups(name), planned_event_settlements(amount, transactions(lifecycle))')
      .gte('due_window_end', from)
      .lte('due_window_start', to)
      .order('due_window_start'),
    client.from('goal_progress').select('*').neq('lifecycle', 'CANCELLED').order('priority'),
    client.from('debt_balances').select('*').neq('lifecycle', 'CANCELLED').order('due_date'),
    client
      .from('notifications')
      .select('*')
      .is('dismissed_at', null)
      .order('created_at', { ascending: false }),
    client
      .from('categories')
      .select('id,name,applicability,is_active')
      .eq('is_active', true)
      .order('name'),
    client
      .from('budget_groups')
      .select('id,name,is_active')
      .eq('is_active', true)
      .order('display_order')
      .order('name'),
    client.from('user_preferences').select('safety_buffer').maybeSingle(),
  ]);

  const failure = [
    plansResult,
    goalsResult,
    debtsResult,
    notificationsResult,
    categoriesResult,
    budgetGroupsResult,
    preferencesResult,
  ]
    .map((result) => result.error)
    .find(Boolean);
  if (failure) throw failure;

  const plans = ((plansResult.data ?? []) as unknown as PlannedEventRow[])
    .filter((row) => row.lifecycle === 'SCHEDULED')
    .map((row): AppPlan => {
      const originalAmount = moneyFromMajor(String(row.amount));
      const settledAmount = sumMoney(
        row.planned_event_settlements
          .filter((settlement) => settlement.transactions?.lifecycle === 'POSTED')
          .map((settlement) => moneyFromMajor(String(settlement.amount))),
      );
      const status: AppPlan['status'] =
        settledAmount >= originalAmount
          ? 'SETTLED'
          : settledAmount > 0
            ? 'PARTIALLY_SETTLED'
            : row.due_window_end < asOf
              ? 'OVERDUE'
              : row.due_window_start <= asOf
                ? 'DUE'
                : 'UPCOMING';
      return {
        id: row.id,
        label: row.description,
        type: row.transaction_type,
        amount: Math.max(0, originalAmount - settledAmount),
        originalAmount,
        date: (row.scheduled_date ??
          (row.transaction_type === 'INCOME'
            ? row.due_window_end
            : row.due_window_start)) as AppPlan['date'],
        accountId: row.account_id ?? row.from_account_id ?? '',
        ...(row.transaction_type === 'TRANSFER' && row.to_account_id
          ? { toAccountId: row.to_account_id }
          : {}),
        group: row.budget_groups?.name ?? 'Ungrouped',
        mandatory: row.commitment_level === 'MANDATORY',
        status,
      };
    });

  const goals = ((goalsResult.data ?? []) as unknown as GoalRow[]).map(
    (row): AppGoal => ({
      id: row.id,
      name: row.name,
      target: moneyFromMajor(String(row.target_amount)),
      allocated: moneyFromMajor(String(row.allocated_amount)),
      targetDate: (row.target_date ?? to) as AppGoal['targetDate'],
      monthlyContribution: moneyFromMajor(String(row.planned_monthly_contribution)),
      priority: row.priority,
      status:
        row.lifecycle === 'COMPLETED'
          ? 'COMPLETED'
          : row.lifecycle === 'PAUSED'
            ? 'PAUSED'
            : 'ACTIVE',
    }),
  );
  const debts = ((debtsResult.data ?? []) as unknown as DebtRow[]).map(
    (row): AppDebt => ({
      id: row.id,
      counterparty: row.counterparty,
      direction: row.direction === 'PAYABLE' ? 'I_OWE' : 'OWED_TO_ME',
      originalAmount: moneyFromMajor(String(row.original_amount)),
      remainingAmount: moneyFromMajor(String(row.remaining_amount)),
      dueDate: (row.due_date ?? to) as AppDebt['dueDate'],
      status:
        Number(row.remaining_amount) === 0
          ? 'SETTLED'
          : Number(row.remaining_amount) < Number(row.original_amount)
            ? 'PARTIAL'
            : row.due_date && row.due_date < asOf
              ? 'OVERDUE'
              : 'OPEN',
    }),
  );
  const notifications = ((notificationsResult.data ?? []) as unknown as NotificationRow[]).map(
    (row) => ({
      id: row.id,
      title: row.title,
      message: row.message,
      severity: row.severity,
      read: row.read_at !== null,
      timestamp: row.created_at,
    }),
  );
  const categories = (
    (categoriesResult.data ?? []) as unknown as Array<{
      id: string;
      name: string;
      applicability: AppCategory['applicability'];
      is_active: boolean;
    }>
  ).map((row) => ({
    id: row.id,
    name: row.name,
    applicability: row.applicability,
    active: row.is_active,
  }));
  const budgetGroups = (
    (budgetGroupsResult.data ?? []) as unknown as Array<{
      id: string;
      name: string;
      is_active: boolean;
    }>
  ).map((row) => ({ id: row.id, name: row.name, active: row.is_active }));
  return {
    accounts,
    transactions,
    plans,
    goals,
    debts,
    notifications,
    categories,
    budgetGroups,
    safetyBuffer: moneyFromMajor(String(preferencesResult.data?.safety_buffer ?? 0)),
  };
}
